/**
 * Helpers UX del panel admin del configurador (Simple UI, soft warnings, reorder).
 * El modo Avanzado fue removido; solo queda el flujo Simple.
 */
import type {
  ConfiguradorCampo,
  ConfiguradorDependencia,
  ConfiguradorOpcion,
} from "@/lib/api";
import { SPECIAL_KEYS, slugFromLabel, uniqueSlug } from "./configuradorProductoShared";

/** Legacy localStorage key del toggle Simple|Avanzado (ya no se usa). */
export const UI_MODE_STORAGE_KEY = "configurador.uiMode";

/** Quita la key legacy del toggle Simple|Avanzado si todavía existe. */
export function clearLegacyUiModeStorage() {
  try {
    localStorage.removeItem(UI_MODE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function slugCampoKey(label: string, existingKeys: Iterable<string>): string {
  return uniqueSlug(slugFromLabel(label), new Set(existingKeys), "campo");
}

export function slugOpcionKey(
  label: string,
  siblings: { key: string }[],
): string {
  return uniqueSlug(
    slugFromLabel(label),
    new Set(siblings.map((s) => s.key)),
    "opcion",
  );
}

export function nextOrden(items: { orden: number }[], step = 10): number {
  if (items.length === 0) return step;
  return Math.max(...items.map((i) => i.orden)) + (step === 1 ? 1 : step);
}

/** Tipos que cargan opciones seleccionables (padres válidos para option_selected). */
export const TIPOS_CON_OPCIONES = new Set([
  "select",
  "select_binario",
  "fk_select",
  "select_ambito",
]);

export function campoTipoConOpciones(tipo: string): boolean {
  return TIPOS_CON_OPCIONES.has(tipo);
}

const TIPOS_NUMERICOS = new Set([
  "decimal_monetario",
  "decimal_cantidad",
  "porcentaje_fijo",
  "decimal_calculado",
  // Alias defensivos (no son choices del backend, pero el form no debe
  // caer en <select> si aparecen).
  "decimal",
  "number",
  "integer",
  "int",
]);

export function campoTipoNumerico(tipo: string): boolean {
  return TIPOS_NUMERICOS.has(tipo);
}

/** Grupos de tipo expuestos en modo Simple (UI limpia). */
export type SimpleCampoTipoGroup = "text" | "number" | "select";

export const SIMPLE_CAMPO_TIPO_OPTIONS: {
  value: SimpleCampoTipoGroup;
  label: string;
  hint: string;
}[] = [
  { value: "text", label: "Texto", hint: "Campo de texto libre" },
  {
    value: "number",
    label: "Número / decimal",
    hint: "Input numérico (no selector)",
  },
  {
    value: "select",
    label: "Select / opciones",
    hint: "Dropdown con opciones",
  },
];

/** Agrupa un tipo backend en la UI Simple. */
export function toSimpleCampoTipoGroup(tipo: string): SimpleCampoTipoGroup {
  if (campoTipoNumerico(tipo)) return "number";
  if (tipo === "text") return "text";
  return "select";
}

/**
 * Resuelve el tipo a persistir desde el grupo Simple.
 * Preserva subtipos (decimal_monetario, select_binario, etc.) si el grupo
 * no cambió — evita romper selects SISTEMA al editar en Simple.
 */
const TIPOS_NUMERICOS_BACKEND = new Set([
  "decimal_monetario",
  "decimal_cantidad",
  "porcentaje_fijo",
  "decimal_calculado",
]);

export function fromSimpleCampoTipoGroup(
  group: SimpleCampoTipoGroup,
  previousTipo: string,
): string {
  if (group === "text") return "text";
  if (group === "number") {
    // Preservar subtipo numérico backend; alias raros → decimal_cantidad.
    if (TIPOS_NUMERICOS_BACKEND.has(previousTipo)) return previousTipo;
    return "decimal_cantidad";
  }
  if (campoTipoConOpciones(previousTipo)) return previousTipo;
  return "select";
}

export type CondicionDepValue =
  | "option_selected"
  | "field_any"
  | "value_gt_zero"
  | "always";

export const CONDICIONES_DEP: {
  value: CondicionDepValue;
  label: string;
}[] = [
  { value: "option_selected", label: "Opción seleccionada" },
  { value: "field_any", label: "Cualquier valor del padre" },
  { value: "value_gt_zero", label: "Valor > 0" },
  { value: "always", label: "Siempre" },
];

/** Condición por defecto según el tipo de campo padre y si tiene opciones. */
export function defaultCondicionParaPadre(args: {
  parentTipo?: string;
  parentHasOptions: boolean;
}): CondicionDepValue {
  if (args.parentHasOptions) return "option_selected";
  if (args.parentTipo && campoTipoNumerico(args.parentTipo)) {
    return "value_gt_zero";
  }
  return "field_any";
}

/**
 * Condiciones ofrecidas en el diálogo. option_selected solo si el padre
 * tiene opciones (o es tipo select); value_gt_zero preferible en numéricos.
 */
export function condicionesParaPadre(args: {
  parentTipo?: string;
  parentHasOptions: boolean;
}): { value: CondicionDepValue; label: string; disabled?: boolean; hint?: string }[] {
  const selectLike = args.parentTipo
    ? campoTipoConOpciones(args.parentTipo)
    : false;
  const optionOk = args.parentHasOptions;
  const numeric = args.parentTipo ? campoTipoNumerico(args.parentTipo) : false;

  return CONDICIONES_DEP.map((c) => {
    if (c.value === "option_selected") {
      if (!optionOk) {
        return {
          ...c,
          disabled: true,
          hint: selectLike
            ? "Este padre aún no tiene opciones activas"
            : "Solo aplica a campos select con opciones",
        };
      }
      return c;
    }
    if (c.value === "value_gt_zero" && args.parentTipo && !numeric && !selectLike) {
      // Texto libre: value_gt_zero no tiene sentido; aún se permite en avanzado
      // vía always/field_any — lo dejamos habilitado pero con hint suave.
      return {
        ...c,
        hint: "Pensado para campos numéricos (cantidad, precio, etc.)",
      };
    }
    return c;
  });
}

export function campoTieneOpcionesActivas(
  campoKey: string,
  opciones: Pick<ConfiguradorOpcion, "campoKey" | "activo">[],
): boolean {
  return opciones.some((o) => o.campoKey === campoKey && o.activo !== false);
}

/**
 * Padre preferido al revelar un tipado (ANCHO/ALTURA).
 * - FAVRE / default: tipología → línea → fábrica → primer select con opciones.
 * - Otras: fábrica primero; tipología/línea se administran tipadas (sin opciones UX).
 */
export function preferredParentForChild(args: {
  childKey: string;
  campos: Pick<ConfiguradorCampo, "key" | "tipo">[];
  opciones: Pick<ConfiguradorOpcion, "campoKey" | "activo">[];
  /** Si es Otras, no preferir selects tipados del catálogo FAVRE. */
  perfil?: "favre" | "otras";
}): Pick<ConfiguradorCampo, "key" | "tipo"> | undefined {
  const { childKey, campos, opciones, perfil } = args;
  const withOptions = campos.filter((c) => {
    if (c.key === childKey) return false;
    // En Otras los dropdowns técnicos se tipan: no son padres option_selected útiles.
    // (fabrica_proveedor y fk proveedor Otras sí mantienen opciones.)
    if (
      perfil === "otras" &&
      c.key !== "fabrica_proveedor" &&
      c.key !== "proveedor_otras_fabricas" &&
      campoTipoConOpciones(c.tipo)
    ) {
      return false;
    }
    return (
      campoTipoConOpciones(c.tipo) &&
      campoTieneOpcionesActivas(c.key, opciones)
    );
  });
  if (withOptions.length === 0) {
    return campos.find((c) => c.key !== childKey);
  }
  const preferOrder =
    perfil === "otras"
      ? (["fabrica_proveedor", "tipologia", "linea"] as const)
      : (["tipologia", "linea", "fabrica_proveedor"] as const);
  for (const key of preferOrder) {
    const hit = withOptions.find((c) => c.key === key);
    if (hit) return hit;
  }
  return withOptions[0];
}

/** Mensaje legible de errores API (DRF field errors / APIError). */
export function formatConfiguradorApiError(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (!error || typeof error !== "object") return fallback;

  const e = error as { message?: unknown; details?: unknown };
  const details = e.details;

  if (typeof details === "string" && details.trim()) {
    return details.trim();
  }

  if (details && typeof details === "object" && !Array.isArray(details)) {
    const parts: string[] = [];
    for (const v of Object.values(details as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item != null && String(item).trim()) parts.push(String(item).trim());
        }
      } else if (typeof v === "string" && v.trim()) {
        parts.push(v.trim());
      }
    }
    if (parts.length) return parts.join(" ");
  }

  if (typeof e.message === "string") {
    const msg = e.message.trim();
    if (msg && msg.toLowerCase() !== "bad request") return msg;
  }

  return fallback;
}

/** Keys raíz conocidas que no necesitan dependencia para aparecer en FAVRE. */
const KNOWN_ROOT_KEYS = new Set([
  "fabrica_proveedor",
  "linea",
  "tipologia",
  "proveedor_otras_fabricas",
  "precio",
  "iva",
  "precio_final_con_iva",
  "observacion_fina",
  "stock_inicial",
  "sucursal_deposito",
]);

export function isSpecialOrSystemKey(key: string): boolean {
  return SPECIAL_KEYS.has(key) || KNOWN_ROOT_KEYS.has(key);
}

/** info = regla sobrante / cosmético; warning = puede ocultar campos o liar el wizard. */
export type SoftWarningSeverity = "info" | "warning";

export type SoftWarning = {
  id: string;
  /** Qué está mal (resumen). */
  message: string;
  /** Cómo arreglarlo (soft: aconsejar, no auto-borrar SISTEMA). */
  advice?: string;
  /** Título corto para badge / panel (opcional). */
  title?: string;
  severity?: SoftWarningSeverity;
  /** Campos involucrados (para resaltar / navegar). */
  campoKeys?: string[];
  /** Id de la dependencia a abrir en el editor, si aplica. */
  depId?: number;
};

export function softWarningsForCampo(args: {
  campo: Pick<ConfiguradorCampo, "key" | "tipo" | "aplicaFavre" | "activo">;
  deps: ConfiguradorDependencia[];
  opciones: ConfiguradorOpcion[];
  isCreate?: boolean;
}): SoftWarning[] {
  const { campo, deps, opciones, isCreate } = args;
  const warnings: SoftWarning[] = [];

  if (campo.aplicaFavre && !isSpecialOrSystemKey(campo.key)) {
    const hasIncoming = deps.some(
      (d) =>
        d.activo !== false &&
        d.childCampoKey === campo.key,
    );
    if (!hasIncoming) {
      warnings.push({
        id: "orphan-favre",
        message:
          "En FAVRE puede no mostrarse hasta tener una regla: definí «aparece cuando…» (p. ej. cuando LINEA tenga valor o elijan una tipología).",
      });
    }
  }

  if (
    (campo.tipo === "select" || campo.tipo === "select_binario") &&
    campo.aplicaFavre
  ) {
    const activeOpts = opciones.filter(
      (o) => o.campoKey === campo.key && o.activo !== false,
    );
    if (activeOpts.length === 0 && !isCreate) {
      warnings.push({
        id: "select-empty",
        message: "Los selects FAVRE necesitan al menos una opción activa.",
      });
    }
  }

  return warnings;
}

export function softWarningsForOpcionDeactivate(args: {
  opcion: ConfiguradorOpcion;
  deps: ConfiguradorDependencia[];
  labelByKey: Map<string, string>;
}): SoftWarning[] {
  const { opcion, deps, labelByKey } = args;
  const childDeps = deps.filter(
    (d) =>
      d.activo !== false &&
      (d.parentOpcionDetail?.key || d.parentOpcionKey) === opcion.key &&
      (d.parentCampoKey || d.parentOpcionDetail?.campoKey) === opcion.campoKey,
  );
  if (childDeps.length === 0) return [];
  const n = childDeps.length;
  const sample = childDeps
    .slice(0, 2)
    .map((d) => labelByKey.get(d.childCampoKey) || d.childCampoKey)
    .join(", ");
  return [
    {
      id: "opt-parent-deps",
      message: `Al desactivarla se ocultan ${n} campo${n === 1 ? "" : "s"} dependiente${n === 1 ? "" : "s"}${sample ? ` (${sample}${n > 2 ? "…" : ""})` : ""}.`,
    },
  ];
}

export function softWarningsForSelectCampo(args: {
  campoKey: string;
  tipo: string;
  aplicaFavre: boolean;
  opciones: ConfiguradorOpcion[];
}): SoftWarning[] {
  const { campoKey, tipo, aplicaFavre, opciones } = args;
  if (!aplicaFavre) return [];
  if (tipo !== "select" && tipo !== "select_binario") return [];
  const active = opciones.filter(
    (o) => o.campoKey === campoKey && o.activo !== false,
  );
  if (active.length > 0) return [];
  return [
    {
      id: "select-empty",
      message: "Los selects FAVRE necesitan al menos una opción activa.",
    },
  ];
}

/** Intercambia orden entre dos ítems adyacentes (PATCH vía caller). */
export function swapOrden<T extends { id: number; orden: number }>(
  items: T[],
  id: number,
  direction: "up" | "down",
): { a: T; b: T } | null {
  const sorted = [...items].sort(
    (x, y) => x.orden - y.orden || x.id - y.id,
  );
  const idx = sorted.findIndex((i) => i.id === id);
  if (idx < 0) return null;
  const swapIdx = direction === "up" ? idx - 1 : idx + 1;
  if (swapIdx < 0 || swapIdx >= sorted.length) return null;
  return { a: sorted[idx], b: sorted[swapIdx] };
}

/** Cadena FAVRE típica (seed SISTEMA): soft warn al reordenar el medio. */
export const FAVRE_CASCADE_KEYS = new Set([
  "fabrica_proveedor",
  "linea",
  "tipologia",
  "mosquitero",
  "tope_mosquitero",
  "color",
]);

/**
 * Campos del wizard “builder” (excl. operativos / grupos virtuales especiales).
 * Siguen listados en admin; el DnD de Fase 1 se enfoca en estos.
 */
export function isWizardBuilderCampo(
  c: Pick<ConfiguradorCampo, "esOperativo" | "esGrupoVirtual" | "key">,
): boolean {
  if (c.esOperativo) return false;
  if (c.esGrupoVirtual && c.key === "proveedor_otras_fabricas") return false;
  return true;
}

export type LabelMaps = {
  campoLabel: (key: string) => string;
  opcionLabel: (campoKey: string, opcionKey: string) => string;
};

function parentKeysOfDep(d: ConfiguradorDependencia): {
  parentKey: string;
  optKey: string;
} {
  const parentKey = d.parentCampoKey || d.parentOpcionDetail?.campoKey || "";
  const optKey = d.parentOpcionDetail?.key || d.parentOpcionKey || "";
  return { parentKey, optKey };
}

/** Chip corto: «cuando FABRICA = OTRAS», «COLOR tiene valor», etc. */
export function humanVisibilityChip(
  d: ConfiguradorDependencia,
  labels: LabelMaps,
): string {
  const { parentKey, optKey } = parentKeysOfDep(d);
  const parentLabel = labels.campoLabel(parentKey) || parentKey || "?";
  if (d.condicion === "option_selected" && optKey) {
    const opt = labels.opcionLabel(parentKey, optKey) || optKey;
    return `${parentLabel} = ${opt}`;
  }
  if (d.condicion === "field_any") {
    return `${parentLabel} tiene valor`;
  }
  if (d.condicion === "value_gt_zero") {
    return `${parentLabel} > 0`;
  }
  if (d.condicion === "always") {
    return `siempre (vía ${parentLabel})`;
  }
  return parentLabel || d.condicion;
}

/** Frase completa: «Mostrar ALTURA cuando en FABRICA elijan OTRAS». */
export function humanVisibilityPhrase(
  d: ConfiguradorDependencia,
  labels: LabelMaps,
): string {
  const { parentKey, optKey } = parentKeysOfDep(d);
  const child = labels.campoLabel(d.childCampoKey) || d.childCampoKey;
  const parent = labels.campoLabel(parentKey) || parentKey || "?";
  if (d.condicion === "option_selected" && optKey) {
    const opt = labels.opcionLabel(parentKey, optKey) || optKey;
    return `Mostrar ${child} cuando en ${parent} elijan ${opt}`;
  }
  if (d.condicion === "field_any") {
    return `Mostrar ${child} cuando ${parent} tenga valor`;
  }
  if (d.condicion === "value_gt_zero") {
    return `Mostrar ${child} cuando ${parent} sea mayor que 0`;
  }
  if (d.condicion === "always") {
    return `Mostrar ${child} siempre (disparador ${parent} no se evalúa)`;
  }
  return `Mostrar ${child} (${d.condicion})`;
}

/** Chip de «aparece cuando» con id de regla (para marcar avisos en el chip). */
export type VisibilityChip = {
  text: string;
  depId?: number;
};

/** Resumen legible de reglas entrantes (chips / fallback). */
export function visibilitySummaryForCampo(args: {
  campo: Pick<
    ConfiguradorCampo,
    "key" | "esRaiz" | "siempreRequerido" | "aplicaFavre" | "aplicaOtras"
  >;
  deps: ConfiguradorDependencia[];
  labels: LabelMaps;
}): { chips: VisibilityChip[]; fallback: string } {
  const { campo, deps, labels } = args;
  const incoming = deps.filter(
    (d) => d.activo !== false && d.childCampoKey === campo.key,
  );
  if (campo.esRaiz) {
    return {
      chips: [{ text: "Siempre (raíz)" }],
      fallback: "Siempre (raíz)",
    };
  }
  if (campo.siempreRequerido && incoming.length === 0) {
    return {
      chips: [{ text: "comercial · siempre requerido" }],
      fallback: "comercial · siempre requerido",
    };
  }
  if (incoming.length === 0) {
    if (campo.aplicaFavre) {
      return {
        chips: [],
        fallback: "sin regla (puede no verse en FAVRE)",
      };
    }
    return { chips: [], fallback: "sin regla de visibilidad" };
  }
  const chips = incoming.map((d) => ({
    text: humanVisibilityChip(d, labels),
    depId: d.id,
  }));
  return {
    chips,
    fallback: chips.map((c) => c.text).join(" · o "),
  };
}

/**
 * Soft guards de una regla (no bloquean SISTEMA; solo avisan).
 */
export function softWarningsForDependencia(args: {
  child: Pick<ConfiguradorCampo, "key" | "orden" | "esRaiz" | "label"> | undefined;
  parent: Pick<ConfiguradorCampo, "key" | "orden" | "label"> | undefined;
  condicion: string;
  parentOpcionKey?: string;
}): SoftWarning[] {
  const { child, parent, condicion, parentOpcionKey } = args;
  const warnings: SoftWarning[] = [];
  if (!child || !parent) return warnings;

  if (child.esRaiz) {
    const always = condicion === "always";
    warnings.push({
      id: always ? "always-useless" : "child-is-root",
      severity: "info",
      title: "Regla sobrante",
      message: always
        ? `«Siempre» hacia ${child.label || child.key} no hace nada: ya es raíz (siempre visible).`
        : `${child.label || child.key} ya es raíz: se ve siempre; esta condición no cambia el Preview.`,
      advice: `Borrá la regla en ${parent.label || parent.key} → «También revela…» (o en ${child.label || child.key} → «Cuándo aparece»).`,
      campoKeys: [child.key, parent.key],
    });
  } else if (condicion === "always") {
    warnings.push({
      id: "always-useless",
      severity: "info",
      title: "Condición «siempre»",
      message: `«Siempre» ignora el valor de ${parent.label || parent.key}.`,
      advice: `En ${child.label || child.key} → «Cuándo aparece», cambiá «siempre» o borrá la regla.`,
      campoKeys: [child.key, parent.key],
    });
  }

  if (
    typeof child.orden === "number" &&
    typeof parent.orden === "number" &&
    child.orden < parent.orden
  ) {
    warnings.push({
      id: "orden-before-parent",
      message: `${child.label || child.key} está antes que ${parent.label || parent.key} en el orden del wizard. El usuario puede no ver el disparador a tiempo.`,
    });
  }

  if (condicion === "option_selected" && !parentOpcionKey) {
    warnings.push({
      id: "option-missing",
      message: "Falta la opción del disparador para esta condición.",
    });
  }

  return warnings;
}

/** Soft warn al mover nodos del medio de la cascada FAVRE/SISTEMA. */
export function softWarningsForCampoReorder(args: {
  moved: Pick<ConfiguradorCampo, "key" | "label" | "sourceId">;
  fromIndex: number;
  toIndex: number;
}): SoftWarning[] {
  const { moved, fromIndex, toIndex } = args;
  if (fromIndex === toIndex) return [];
  if (!FAVRE_CASCADE_KEYS.has(moved.key) && !moved.sourceId) return [];
  if (!FAVRE_CASCADE_KEYS.has(moved.key)) return [];
  return [
    {
      id: "favre-cascade-move",
      message: `Moviste ${moved.label || moved.key} (cadena FAVRE/SISTEMA). Revisá el Preview: el orden afecta el clear al cambiar selects.`,
    },
  ];
}

/**
 * Tras un DnD, reasigna los valores de `orden` existentes a la nueva secuencia.
 * Devuelve solo los pares que cambian (para PATCH por campo).
 */
export function planCampoOrdenPatches<
  T extends { id: number; key: string; orden: number },
>(sortedBefore: T[], fromIndex: number, toIndex: number): { id: number; orden: number }[] {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= sortedBefore.length ||
    toIndex >= sortedBefore.length ||
    fromIndex === toIndex
  ) {
    return [];
  }
  const ordenValues = sortedBefore.map((c) => c.orden);
  const reordered = [...sortedBefore];
  const [item] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, item);
  const patches: { id: number; orden: number }[] = [];
  for (let i = 0; i < reordered.length; i++) {
    const nextOrden = ordenValues[i];
    if (reordered[i].orden !== nextOrden) {
      patches.push({ id: reordered[i].id, orden: nextOrden });
    }
  }
  return patches;
}

/**
 * Fase 2 — orden sugerido al insertar un campo en `insertIndex` (0 = arriba).
 * Si no hay hueco entre vecinos, devuelve `prev + 1` (el caller puede
 * rebalancear con `planInsertCampoOrdenPatches`).
 */
export function computeInsertOrden(
  sorted: { orden: number }[],
  insertIndex: number,
): number {
  if (sorted.length === 0) return 10;
  if (insertIndex <= 0) {
    const first = sorted[0].orden;
    return first > 10 ? first - 10 : Math.max(1, first - 1);
  }
  if (insertIndex >= sorted.length) {
    return nextOrden(sorted, 10);
  }
  const prev = sorted[insertIndex - 1].orden;
  const next = sorted[insertIndex].orden;
  if (next - prev >= 2) {
    return Math.floor((prev + next) / 2);
  }
  return prev + 1;
}

/**
 * Tras crear un campo con `orden` en una posición, desplaza hacia arriba los
 * que quedaron con el mismo u orden ≤ (colisión). Solo PATCH de afectados.
 */
export function planInsertCampoOrdenPatches<
  T extends { id: number; key: string; orden: number },
>(
  sortedAfterCreate: T[],
  createdKey: string,
  desiredIndex: number,
): { id: number; orden: number }[] {
  const idx = sortedAfterCreate.findIndex((c) => c.key === createdKey);
  if (idx < 0) return [];
  const target = Math.max(0, Math.min(desiredIndex, sortedAfterCreate.length - 1));
  if (idx === target) {
    // Puede haber colisión de orden con el vecino: reasignar secuencia limpia.
    const colliding = sortedAfterCreate.some(
      (c, i) => i !== idx && c.orden === sortedAfterCreate[idx].orden,
    );
    if (!colliding) return [];
  }
  return planCampoOrdenPatches(sortedAfterCreate, idx, target);
}

function parentKeyOfDep(d: ConfiguradorDependencia): string {
  return d.parentCampoKey || d.parentOpcionDetail?.campoKey || "";
}

/** Grafo parent→child de deps activas (visibilidad). */
export function buildVisibilityAdj(
  deps: ConfiguradorDependencia[],
): Map<string, string[]> {
  const adj = new Map<string, string[]>();
  for (const d of deps) {
    if (d.activo === false) continue;
    const parent = parentKeyOfDep(d);
    const child = d.childCampoKey;
    if (!parent || !child) continue;
    const list = adj.get(parent) || [];
    list.push(child);
    adj.set(parent, list);
  }
  return adj;
}

/**
 * ¿El campo es alcanzable desde alguna raíz vía el grafo parent→child?
 * Con DNF, si ya hay un camino desde raíz, una arista extra que cierre un
 * ciclo estructural sigue siendo bootstrappable (soft), no deadlock duro.
 */
export function isCampoReachableFromRoots(
  deps: ConfiguradorDependencia[],
  rootCampoKeys: Iterable<string>,
  targetKey: string,
): boolean {
  if (!targetKey) return false;
  const roots = [...rootCampoKeys].filter(Boolean);
  if (roots.includes(targetKey)) return true;
  const adj = buildVisibilityAdj(deps);
  const seen = new Set<string>();
  const queue = [...roots];
  for (const r of roots) seen.add(r);
  while (queue.length) {
    const cur = queue.shift()!;
    for (const next of adj.get(cur) || []) {
      if (seen.has(next)) continue;
      if (next === targetKey) return true;
      seen.add(next);
      queue.push(next);
    }
  }
  return false;
}

export type GraphCycleIssue = {
  id: string;
  cycle: string[];
  message: string;
  advice: string;
};

/** Fase 3 — ciclos en el grafo parent→child (detección estructural). */
export function detectDependencyCycles(
  deps: ConfiguradorDependencia[],
  labels?: (key: string) => string,
): GraphCycleIssue[] {
  const adj = buildVisibilityAdj(deps);
  const labelOf = labels || ((k: string) => k);
  const issues: GraphCycleIssue[] = [];
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const stack: string[] = [];

  const dfs = (node: string) => {
    if (visited.has(node)) return;
    if (visiting.has(node)) {
      const start = stack.indexOf(node);
      const cycle =
        start >= 0 ? [...stack.slice(start), node] : [node, node];
      const key = cycle.join("→");
      if (!issues.some((i) => i.id === `cycle:${key}`)) {
        const path = cycle.map(labelOf).join(" → ");
        issues.push({
          id: `cycle:${key}`,
          cycle,
          message: `Ciclo de visibilidad: ${path}.`,
          advice:
            "Quitá o reorientá una de las reglas del ciclo (en «Cuándo aparece» de esos campos). No se borran reglas SISTEMA automáticamente.",
        });
      }
      return;
    }
    visiting.add(node);
    stack.push(node);
    for (const next of adj.get(node) || []) {
      dfs(next);
    }
    stack.pop();
    visiting.delete(node);
    visited.add(node);
  };

  for (const node of adj.keys()) {
    dfs(node);
  }
  // Also visit children that only appear as targets
  for (const list of adj.values()) {
    for (const child of list) {
      if (!visited.has(child)) dfs(child);
    }
  }
  return issues;
}

export type DeadBranchIssue = {
  id: string;
  campoKey: string;
  parentKey?: string;
  depId?: number;
  message: string;
  advice: string;
};

/**
 * Fase 3 — campos no-raíz con deps cuyo padre nunca es alcanzable desde
 * una raíz (o el propio campo no es alcanzable). Soft warn.
 */
export function detectDeadBranches(args: {
  deps: ConfiguradorDependencia[];
  campos: Pick<
    ConfiguradorCampo,
    "key" | "esRaiz" | "activo" | "label" | "aplicaFavre"
  >[];
  labels?: (key: string) => string;
}): DeadBranchIssue[] {
  const { deps, campos, labels } = args;
  const labelOf = labels || ((k: string) => k);
  const activeCampos = campos.filter((c) => c.activo !== false);
  const byKey = new Map(activeCampos.map((c) => [c.key, c]));
  const adj = buildVisibilityAdj(deps);
  const roots = activeCampos
    .filter((c) => c.esRaiz || c.key === "fabrica_proveedor")
    .map((c) => c.key);

  const reachable = new Set<string>();
  const queue = [...roots];
  for (const r of roots) reachable.add(r);
  while (queue.length) {
    const cur = queue.shift()!;
    for (const next of adj.get(cur) || []) {
      if (!reachable.has(next)) {
        reachable.add(next);
        queue.push(next);
      }
    }
  }

  const issues: DeadBranchIssue[] = [];
  for (const d of deps) {
    if (d.activo === false) continue;
    const parent = parentKeyOfDep(d);
    const child = d.childCampoKey;
    if (!parent || !child) continue;
    const childCampo = byKey.get(child);
    if (!childCampo || childCampo.esRaiz) continue;
    if (!byKey.has(parent)) {
      issues.push({
        id: `dead-missing-parent:${d.id}`,
        campoKey: child,
        parentKey: parent,
        depId: d.id,
        message: `${labelOf(child)} depende de ${parent} (inexistente/inactivo).`,
        advice: `Activá o restaurá el disparador, o cambiá la regla de «Cuándo aparece» de ${labelOf(child)} a un campo válido.`,
      });
      continue;
    }
    if (!reachable.has(parent) && !roots.includes(parent)) {
      issues.push({
        id: `dead-parent:${d.id}`,
        campoKey: child,
        parentKey: parent,
        depId: d.id,
        message: `Rama muerta: ${labelOf(child)} ← ${labelOf(parent)} (padre no alcanzable desde raíz).`,
        advice: `Conectá ${labelOf(parent)} a una raíz (o a un campo alcanzable), o quitá/cambiá la regla que revela ${labelOf(child)}. No se borran reglas SISTEMA automáticamente.`,
      });
    }
  }
  return issues;
}

function depsOnEdge(
  deps: ConfiguradorDependencia[],
  from: string,
  to: string,
): ConfiguradorDependencia[] {
  return deps.filter(
    (d) =>
      d.activo !== false &&
      parentKeyOfDep(d) === from &&
      d.childCampoKey === to,
  );
}

/**
 * Reglas que «muestran» una raíz: no cambian el Preview (la raíz ya es
 * siempre visible). Caso típico: COLOR → FABRICA con condición «siempre».
 */
export function detectUselessRootVisibilityRules(args: {
  deps: ConfiguradorDependencia[];
  campos: Pick<ConfiguradorCampo, "key" | "esRaiz" | "activo" | "label">[];
  labels?: (key: string) => string;
}): SoftWarning[] {
  const { deps, campos, labels } = args;
  const labelOf = labels || ((k: string) => k);
  const byKey = new Map(
    campos.filter((c) => c.activo !== false).map((c) => [c.key, c]),
  );
  const out: SoftWarning[] = [];
  for (const d of deps) {
    if (d.activo === false) continue;
    const parent = parentKeyOfDep(d);
    const child = d.childCampoKey;
    if (!parent || !child) continue;
    const childCampo = byKey.get(child);
    if (!childCampo?.esRaiz) continue;
    const parentLabel = labelOf(parent);
    const childLabel = labelOf(child);
    const always = d.condicion === "always";
    out.push({
      id: `useless-root:${d.id}`,
      severity: "info",
      title: "Regla sobrante",
      depId: d.id,
      campoKeys: [parent, child],
      message: always
        ? `${parentLabel} → ${childLabel} «siempre»: ${childLabel} ya es raíz, la regla sobra.`
        : `${parentLabel} → ${childLabel}: ${childLabel} ya es raíz y se ve siempre.`,
      advice: `Borrá la regla en ${parentLabel} → «También revela…» (muestra ${childLabel}${always ? " siempre" : ""}) o en ${childLabel} → «Cuándo aparece».`,
    });
  }
  return out;
}

function softWarningFromCycle(args: {
  cycle: string[];
  deps: ConfiguradorDependencia[];
  campos: Pick<ConfiguradorCampo, "key" | "esRaiz" | "activo" | "label">[];
  labels: (key: string) => string;
}): SoftWarning | null {
  const { cycle, deps, campos, labels } = args;
  const byKey = new Map(
    campos.filter((c) => c.activo !== false).map((c) => [c.key, c]),
  );
  const uniqueKeys = [...new Set(cycle)];
  const path = uniqueKeys.map(labels).join(" → ");

  // Aristas del ciclo; priorizar las que cierran hacia una raíz (ruido típico).
  const edgeDeps: ConfiguradorDependencia[] = [];
  for (let i = 0; i < cycle.length - 1; i++) {
    edgeDeps.push(...depsOnEdge(deps, cycle[i], cycle[i + 1]));
  }
  const rootClosing = edgeDeps.filter((d) => byKey.get(d.childCampoKey)?.esRaiz);
  if (rootClosing.length > 0) {
    // Cubierto por detectUselessRootVisibilityRules; no duplicar drama de «ciclo».
    return null;
  }

  const sampleParents = uniqueKeys
    .slice(0, 3)
    .map(labels)
    .join(", ");
  return {
    id: `cycle:${cycle.join("→")}`,
    severity: "warning",
    title: "Reglas en bucle",
    campoKeys: uniqueKeys,
    message: `Hay un bucle entre reglas de «cuándo aparece»: ${path}. Un campo termina dependiendo de otro que, más adelante, vuelve a depender del primero.`,
    advice: `Esto sí puede liar el wizard. Abrí «Cuándo aparece» en uno de estos campos (${sampleParents}${uniqueKeys.length > 3 ? "…" : ""}) y borrá o reorientá una regla para cortar el bucle. El Preview a veces sigue «andando» si las raíces ya están visibles, pero conviene limpiarlo.`,
  };
}

function softWarningFromDeadBranch(
  d: DeadBranchIssue,
  labels: (key: string) => string,
): SoftWarning {
  const childLabel = labels(d.campoKey);
  const parentLabel = d.parentKey ? labels(d.parentKey) : d.parentKey || "?";
  if (d.id.startsWith("dead-missing-parent:")) {
    return {
      id: d.id,
      severity: "warning",
      title: "Disparador faltante",
      campoKeys: d.parentKey ? [d.campoKey, d.parentKey] : [d.campoKey],
      depId: d.depId,
      message: `${childLabel} espera un disparador (${d.parentKey}) que no está activo o no existe. En el wizard puede no aparecer.`,
      advice: `Abrí ${childLabel} → «Cuándo aparece», y cambiá la condición a un campo válido o borrá la regla rota.`,
    };
  }
  return {
    id: d.id,
    severity: "warning",
    title: "Campo que puede no verse",
    campoKeys: d.parentKey ? [d.campoKey, d.parentKey] : [d.campoKey],
    depId: d.depId,
    message: `${childLabel} solo se revela desde ${parentLabel}, pero ${parentLabel} no está conectado a FABRICA (ni a otra raíz). En el Preview / wizard puede quedar oculto para siempre.`,
    advice: `Opción A: en ${parentLabel} → «Cuándo aparece», agregá una condición desde un campo que sí se ve (p. ej. FABRICA o LINEA). Opción B: en ${childLabel} → «Cuándo aparece», cambiá o borrá la regla que depende de ${parentLabel}.`,
  };
}

/**
 * Soft warn si la regla nueva cierra un ciclo estructural pero el hijo ya
 * arranca desde una raíz (OR bootstrappable). No bloquea el save.
 */
export function softCycleWarningForNewDependencia(args: {
  deps: ConfiguradorDependencia[];
  parentCampoKey: string;
  childCampoKey: string;
  editingDepId?: number | null;
  labels?: (key: string) => string;
  rootCampoKeys?: Iterable<string>;
}): SoftWarning | null {
  const {
    deps,
    parentCampoKey,
    childCampoKey,
    editingDepId,
    labels,
    rootCampoKeys,
  } = args;
  if (!parentCampoKey || !childCampoKey) return null;
  if (parentCampoKey === childCampoKey) return null;
  if (hardBlockReasonForDependencia(args)) return null;

  const roots = new Set(rootCampoKeys || []);
  if (roots.has(childCampoKey)) return null;
  const others = deps.filter(
    (d) =>
      d.activo !== false &&
      !roots.has(d.childCampoKey) &&
      (editingDepId == null || d.id !== editingDepId),
  );
  if (!isCampoReachableFromRoots(others, roots, childCampoKey)) return null;

  const without = detectDependencyCycles(others);
  const withEdge = detectDependencyCycles([
    ...others,
    {
      id: -1,
      parentCampoKey,
      childCampoKey,
      parentOpcionKey: null,
      condicion: "field_any",
      orden: 0,
      activo: true,
    } as ConfiguradorDependencia,
  ]);
  if (withEdge.length <= without.length) return null;
  const labelOf = labels || ((k: string) => k);
  const added = withEdge.find((i) => !without.some((w) => w.id === i.id));
  const path =
    added?.cycle.map(labelOf).join(" → ") ||
    `${labelOf(parentCampoKey)} → ${labelOf(childCampoKey)} → …`;
  return {
    id: "soft-cycle-bootstrap",
    severity: "warning",
    title: "Bucle con alternativa OR",
    campoKeys: [childCampoKey, parentCampoKey],
    message: `Ciclo estructural (${path}), pero ${labelOf(childCampoKey)} ya tiene otra vía desde una raíz: se puede guardar como alternativa OR.`,
  };
}

/**
 * Hard block solo si la regla **nueva** (o un cambio de parent/child)
 * introduce un ciclo **no bootstrappable**. Con DNF, si el hijo ya es
 * alcanzable desde una raíz por otra alternativa OR, el ciclo estructural
 * es soft (aviso) — no bloquea. Editar una dep legacy sin cambiar extremos
 * → no bloquea (soft warn en panel grafo).
 */
export function hardBlockReasonForDependencia(args: {
  deps: ConfiguradorDependencia[];
  parentCampoKey: string;
  childCampoKey: string;
  editingDepId?: number | null;
  labels?: (key: string) => string;
  rootCampoKeys?: Iterable<string>;
}): string | null {
  const {
    deps,
    parentCampoKey,
    childCampoKey,
    editingDepId,
    labels,
    rootCampoKeys,
  } = args;
  if (!parentCampoKey || !childCampoKey) return null;
  if (parentCampoKey === childCampoKey) {
    return "El campo hijo no puede ser el mismo que el padre";
  }

  if (editingDepId != null) {
    const original = deps.find((d) => d.id === editingDepId);
    const origParent = original ? parentKeyOfDep(original) : "";
    if (
      original &&
      origParent === parentCampoKey &&
      original.childCampoKey === childCampoKey
    ) {
      // Mismos extremos: legacy / no es un ciclo nuevo.
      return null;
    }
  }

  const roots = new Set(rootCampoKeys || []);
  // Una dependencia hacia una raíz es semánticamente inerte: campo_visible
  // devuelve true antes de mirar deps. Se conserva como aviso legacy, pero no
  // debe fabricar ciclos ni bloquear reglas nuevas válidas.
  const others = deps.filter(
    (d) =>
      d.activo !== false &&
      !roots.has(d.childCampoKey) &&
      (editingDepId == null || d.id !== editingDepId),
  );
  if (roots.has(childCampoKey)) return null;

  // DNF: hijo ya visible vía otra rama desde raíz → ciclo soft, no hard.
  if (isCampoReachableFromRoots(others, roots, childCampoKey)) {
    return null;
  }

  const without = detectDependencyCycles(others);
  const withEdge = detectDependencyCycles([
    ...others,
    {
      id: -1,
      parentCampoKey,
      childCampoKey,
      parentOpcionKey: null,
      condicion: "field_any",
      orden: 0,
      activo: true,
    } as ConfiguradorDependencia,
  ]);
  if (withEdge.length > without.length) {
    const labelOf = labels || ((k: string) => k);
    const added = withEdge.find(
      (i) => !without.some((w) => w.id === i.id),
    );
    return (
      added?.message ||
      `Esta regla crea un ciclo (${labelOf(parentCampoKey)} → ${labelOf(childCampoKey)}).`
    );
  }
  return null;
}

/** Issues de visibilidad para el panel F3 (reglas sobrantes + bucles + ramas muertas). */
export function graphIssuesForBuilder(args: {
  deps: ConfiguradorDependencia[];
  campos: Pick<
    ConfiguradorCampo,
    "key" | "esRaiz" | "activo" | "label" | "aplicaFavre"
  >[];
  labels?: (key: string) => string;
}): SoftWarning[] {
  const { deps, campos, labels } = args;
  const labelOf = labels || ((k: string) => k);
  const warnings: SoftWarning[] = [];

  for (const w of detectUselessRootVisibilityRules({
    deps,
    campos,
    labels: labelOf,
  })) {
    warnings.push(w);
  }

  for (const c of detectDependencyCycles(deps, labelOf)) {
    const soft = softWarningFromCycle({
      cycle: c.cycle,
      deps,
      campos,
      labels: labelOf,
    });
    if (soft) warnings.push(soft);
  }

  for (const d of detectDeadBranches({ deps, campos, labels: labelOf })) {
    warnings.push(softWarningFromDeadBranch(d, labelOf));
  }

  return warnings;
}

/** Agrupa issues por key de campo (badge + tooltip). */
export function graphIssuesByCampoKey(args: {
  deps: ConfiguradorDependencia[];
  campos: Pick<
    ConfiguradorCampo,
    "key" | "esRaiz" | "activo" | "label" | "aplicaFavre"
  >[];
  labels?: (key: string) => string;
}): Map<string, SoftWarning[]> {
  const byKey = new Map<string, SoftWarning[]>();
  const push = (key: string, w: SoftWarning) => {
    if (!key) return;
    const list = byKey.get(key);
    if (list) {
      if (!list.some((x) => x.id === w.id)) list.push(w);
    } else {
      byKey.set(key, [w]);
    }
  };

  for (const w of graphIssuesForBuilder(args)) {
    const keys =
      w.campoKeys && w.campoKeys.length > 0
        ? w.campoKeys
        : [];
    if (keys.length === 0) continue;
    for (const k of keys) push(k, w);
  }
  return byKey;
}

/**
 * Agrupa avisos por id de dependencia (tag en la regla / chip).
 * Incluye issues del grafo con depId + soft warnings por regla.
 */
export function graphIssuesByDepId(args: {
  deps: ConfiguradorDependencia[];
  campos: Pick<
    ConfiguradorCampo,
    "key" | "esRaiz" | "activo" | "label" | "aplicaFavre" | "orden"
  >[];
  labels?: (key: string) => string;
}): Map<number, SoftWarning[]> {
  const byDep = new Map<number, SoftWarning[]>();
  const push = (depId: number, w: SoftWarning) => {
    if (!depId) return;
    const list = byDep.get(depId);
    if (list) {
      if (!list.some((x) => x.id === w.id)) list.push(w);
    } else {
      byDep.set(depId, [w]);
    }
  };

  for (const w of graphIssuesForBuilder(args)) {
    if (w.depId != null) push(w.depId, { ...w, depId: w.depId });
  }

  const byKey = new Map(
    args.campos.filter((c) => c.activo !== false).map((c) => [c.key, c]),
  );
  for (const d of args.deps) {
    if (d.activo === false) continue;
    const parentKey = parentKeyOfDep(d);
    const child = byKey.get(d.childCampoKey);
    const parent = byKey.get(parentKey);
    const existing = byDep.get(d.id) || [];
    const hasUselessRoot = existing.some((e) =>
      e.id.startsWith("useless-root:"),
    );
    for (const w of softWarningsForDependencia({
      child,
      parent,
      condicion: d.condicion,
      parentOpcionKey: d.parentOpcionDetail?.key || d.parentOpcionKey || "",
    })) {
      if (w.id === "option-missing") continue;
      if (
        hasUselessRoot &&
        (w.id === "child-is-root" || w.id === "always-useless")
      ) {
        continue;
      }
      push(d.id, {
        ...w,
        depId: d.id,
        campoKeys: w.campoKeys || [d.childCampoKey, parentKey].filter(Boolean),
      });
    }
  }
  return byDep;
}

/** Severidad dominante de una lista (warning > info). */
export function dominantGraphIssueSeverity(
  issues: SoftWarning[],
): SoftWarningSeverity {
  if (issues.some((w) => (w.severity || "warning") === "warning")) {
    return "warning";
  }
  return "info";
}

/** Texto corto para tooltip del badge de avisos. */
export function formatGraphIssueTooltip(issues: SoftWarning[]): string {
  if (issues.length === 0) {
    return "Hay un aviso sobre las reglas de «cuándo aparece».";
  }
  return issues
    .map((w) => {
      const head = w.title ? `${w.title}: ${w.message}` : w.message;
      return w.advice ? `${head}\n→ ${w.advice}` : head;
    })
    .join("\n\n");
}

/** Tooltip compacto para el tag sobre una regla concreta. */
export function formatRuleIssueTooltip(issues: SoftWarning[]): string {
  if (issues.length === 0) return "";
  return issues
    .map((w) => {
      const head = w.title || "Aviso";
      const body = w.advice || w.message;
      return body ? `${head}\n${body}` : head;
    })
    .join("\n\n");
}

/** Etiqueta corta del badge según severidad. */
export function graphIssueBadgeLabel(issues: SoftWarning[]): string {
  if (issues.length === 0) return "aviso";
  const sev = dominantGraphIssueSeverity(issues);
  if (sev === "info" && issues.every((w) => w.severity === "info")) {
    return issues.length === 1 ? "regla sobrante" : "reglas sobrantes";
  }
  return issues.length === 1 ? "revisar regla" : "revisar reglas";
}

/** Tag corto sobre una regla/chip (no el badge de fila). */
export function ruleIssueTagLabel(issues: SoftWarning[]): string {
  if (issues.length === 0) return "aviso";
  const sev = dominantGraphIssueSeverity(issues);
  if (sev === "info" && issues.every((w) => (w.severity || "info") === "info")) {
    return "sobrante";
  }
  return "revisar";
}

/** Condiciones en lenguaje humano (Simple mode). */
export const CONDICIONES_HUMANAS: {
  value: CondicionDepValue;
  label: string;
  hint?: string;
}[] = [
  { value: "option_selected", label: "elijan opción" },
  { value: "field_any", label: "tenga valor" },
  { value: "value_gt_zero", label: "sea mayor que 0" },
  {
    value: "always",
    label: "siempre (evitar)",
    hint: "Ignora el disparador. Si el campo destino ya es raíz (p. ej. FABRICA), la regla sobra y no cambia el Preview.",
  },
];

// ---------------------------------------------------------------------------
// Opción C — Híbrido: perfil por fábrica/proveedor + bloque compartido
// Membresía MVP = flags aplica_favre / aplica_otras (sin modelo ConfiguradorPerfil).
//
// Stub futuro N proveedores (NO implementar CRUD aquí):
//   type ConfiguradorPerfilId = string; // hoy: "favre" | "otras"
//   CONFIGURADOR_PERFILES → catálogo desde API (id, label, fabricaKey)
//   campoPerteneceAPerfil → M2M / aplica_perfiles[] en lugar de booleanos duales
//   partitionCamposForPerfilBuilder / filterDepsForPerfil se mantienen como API FE
// Ver docs/PLAN_CONFIGURADOR_PERFIL_HIBRIDO.md §5.
// ---------------------------------------------------------------------------

export const PERFIL_STORAGE_KEY = "configurador.perfil";

/** MVP: solo dos perfiles. Futuro N: ampliar a string de API (ver stub arriba). */
export type ConfiguradorPerfilId = "favre" | "otras";

export type ConfiguradorPerfilMeta = {
  id: ConfiguradorPerfilId;
  label: string;
  /** Key de opción en fabrica_proveedor. */
  fabricaKey: string;
  shortLabel: string;
};

export const CONFIGURADOR_PERFILES: ConfiguradorPerfilMeta[] = [
  {
    id: "favre",
    label: "Perfil FAVRE",
    shortLabel: "FAVRE",
    fabricaKey: "favre",
  },
  {
    id: "otras",
    label: "Perfil Otras fábricas",
    shortLabel: "Otras",
    fabricaKey: "otras_fabricas",
  },
];

export const PASO0_FABRICA_KEY = "fabrica_proveedor";

/** Bloque comercial/stock compartido (una sola definición SISTEMA). */
export const SHARED_COMMERCIAL_KEYS = new Set([
  "precio",
  "iva",
  "precio_final_con_iva",
  "observacion_fina",
  "stock_inicial",
  "sucursal_deposito",
]);

/**
 * Catálogo SISTEMA reutilizable en ambos perfiles (sin clonar).
 * El resto de la cascada FAVRE se oculta en el builder Otras para no mezclar grafos.
 */
export const SISTEMA_SHARED_CATALOG_KEYS = new Set(["linea", "tipologia"]);

export type CampoPerfilSection = "paso0" | "rama" | "shared";

export function readPerfil(): ConfiguradorPerfilId {
  try {
    const v = localStorage.getItem(PERFIL_STORAGE_KEY);
    if (v === "otras" || v === "favre") return v;
  } catch {
    /* ignore */
  }
  return "favre";
}

export function writePerfil(perfil: ConfiguradorPerfilId) {
  try {
    localStorage.setItem(PERFIL_STORAGE_KEY, perfil);
  } catch {
    /* ignore */
  }
}

export function perfilMeta(perfil: ConfiguradorPerfilId): ConfiguradorPerfilMeta {
  return (
    CONFIGURADOR_PERFILES.find((p) => p.id === perfil) || CONFIGURADOR_PERFILES[0]
  );
}

export function fabricaKeyForPerfil(perfil: ConfiguradorPerfilId): string {
  return perfilMeta(perfil).fabricaKey;
}

export function perfilFromFabricaKey(key: string): ConfiguradorPerfilId | null {
  const k = (key || "").trim().toLowerCase();
  if (!k) return null;
  if (k === "favre" || /favre/.test(k)) return "favre";
  if (k === "otras_fabricas" || /otras/.test(k)) return "otras";
  return null;
}

export function isSharedCommercialCampo(c: { key: string }): boolean {
  return SHARED_COMMERCIAL_KEYS.has(c.key);
}

export function isPaso0Campo(c: { key: string }): boolean {
  return c.key === PASO0_FABRICA_KEY;
}

/** Rama técnica (no paso 0, no bloque comercial). */
export function isGraphRamaCampo(c: { key: string }): boolean {
  return !isPaso0Campo(c) && !isSharedCommercialCampo(c);
}

/**
 * ¿El campo entra en la vista del perfil?
 * Membresía = PerfilCampo (`membershipPerfiles`). Fallback a aplica_* si el
 * payload viejo no trae membresía. Paso0 / comercial: siempre.
 */
export function campoPerteneceAPerfil(
  c: Pick<
    ConfiguradorCampo,
    "key" | "aplicaFavre" | "aplicaOtras" | "membershipPerfiles"
  >,
  perfil: ConfiguradorPerfilId,
): boolean {
  if (isPaso0Campo(c) || isSharedCommercialCampo(c)) return true;
  if (Array.isArray(c.membershipPerfiles)) {
    return c.membershipPerfiles.includes(perfil);
  }
  if (perfil === "favre") return !!c.aplicaFavre;
  return !!c.aplicaOtras;
}

/** Visible en el perfil activo (PerfilCampo.activo). Comercial/paso0: campo.activo. */
export function campoVisibleEnPerfil(
  c: Pick<ConfiguradorCampo, "key" | "activo" | "visiblePorPerfil">,
  perfil: ConfiguradorPerfilId,
): boolean {
  if (isPaso0Campo(c) || isSharedCommercialCampo(c)) {
    return c.activo !== false;
  }
  const map = c.visiblePorPerfil;
  if (map && Object.prototype.hasOwnProperty.call(map, perfil)) {
    return !!map[perfil];
  }
  return c.activo !== false;
}

/**
 * Tipo efectivo en Simple: Otras tipa dropdowns técnicos (sin administrar opciones).
 * Comercial / paso0 conservan su tipo canónico.
 */
export function effectiveTipoForPerfil(
  c: Pick<ConfiguradorCampo, "key" | "tipo">,
  perfil: ConfiguradorPerfilId,
): string {
  if (perfil !== "otras") return c.tipo;
  if (isPaso0Campo(c) || isSharedCommercialCampo(c)) return c.tipo;
  if (campoTipoConOpciones(c.tipo)) return "text";
  return c.tipo;
}

/** True si en este perfil el campo se administra como tipado (sin opciones). */
export function isTipadoInPerfil(
  c: Pick<ConfiguradorCampo, "key" | "tipo">,
  perfil: ConfiguradorPerfilId,
): boolean {
  return !campoTipoConOpciones(effectiveTipoForPerfil(c, perfil));
}

/** Resuelve perfil_id para evaluar(?perfil_id=) desde key de fábrica. */
export function perfilIdForFabricaKey(
  fabricaKey: string | null | undefined,
): ConfiguradorPerfilId | null {
  return perfilFromFabricaKey(fabricaKey || "");
}

export function sectionForCampoInPerfil(
  c: Pick<
    ConfiguradorCampo,
    "key" | "aplicaFavre" | "aplicaOtras" | "membershipPerfiles"
  >,
  perfil: ConfiguradorPerfilId,
): CampoPerfilSection | null {
  if (!campoPerteneceAPerfil(c, perfil)) return null;
  if (isPaso0Campo(c)) return "paso0";
  if (isSharedCommercialCampo(c)) return "shared";
  return "rama";
}

export function defaultAplicaFlagsForPerfil(perfil: ConfiguradorPerfilId): {
  aplicaFavre: boolean;
  aplicaOtras: boolean;
} {
  if (perfil === "favre") return { aplicaFavre: true, aplicaOtras: false };
  return { aplicaFavre: false, aplicaOtras: true };
}

function opcionKeyOfDep(d: ConfiguradorDependencia): string {
  return d.parentOpcionDetail?.key || d.parentOpcionKey || "";
}

type CampoPerfilPick = Pick<
  ConfiguradorCampo,
  "key" | "aplicaFavre" | "aplicaOtras" | "esRaiz" | "membershipPerfiles"
>;

/** Extremo técnico con membresía en ambos perfiles (post-paridad F2). */
export function campoDualMembership(c: CampoPerfilPick): boolean {
  if (isPaso0Campo(c) || isSharedCommercialCampo(c)) return false;
  if (Array.isArray(c.membershipPerfiles)) {
    return (
      c.membershipPerfiles.includes("favre") &&
      c.membershipPerfiles.includes("otras")
    );
  }
  return !!c.aplicaFavre && !!c.aplicaOtras;
}

/** Extremo solo de este perfil (o ancla paso0 / comercial compartido). */
export function campoExclusiveToPerfil(
  c: CampoPerfilPick,
  perfil: ConfiguradorPerfilId,
): boolean {
  if (isPaso0Campo(c) || isSharedCommercialCampo(c)) return true;
  if (Array.isArray(c.membershipPerfiles)) {
    return (
      c.membershipPerfiles.includes(perfil) &&
      !c.membershipPerfiles.includes(perfil === "favre" ? "otras" : "favre")
    );
  }
  if (perfil === "favre") return !!c.aplicaFavre && !c.aplicaOtras;
  return !!c.aplicaOtras && !c.aplicaFavre;
}

/**
 * Criterio para deps legacy `perfil=null` en Simple (fail-closed):
 * - No listar en ambos perfiles a ciegas.
 * - Si algún extremo es exclusivo del perfil → mostrar solo ahí.
 * - Si ambos extremos son duales (aplicaFavre∧aplicaOtras) → cascada legacy
 *   FAVRE: visible en FAVRE, nunca en Otras (evita chips/amarillos falsos
 *   tipo premarco→color→altura). Runtime PerfilRegla usa el mismo corte.
 */
export function legacyNullDepRelevantToPerfil(
  parent: CampoPerfilPick | undefined,
  child: CampoPerfilPick | undefined,
  perfil: ConfiguradorPerfilId,
): boolean {
  if (!child || !campoPerteneceAPerfil(child, perfil)) return false;
  if (parent && !campoPerteneceAPerfil(parent, perfil)) return false;

  if (
    (parent && campoExclusiveToPerfil(parent, perfil)) ||
    campoExclusiveToPerfil(child, perfil)
  ) {
    return true;
  }

  if (parent && campoDualMembership(parent) && campoDualMembership(child)) {
    return perfil === "favre";
  }

  return false;
}

/**
 * Regla visible en la UX del perfil (aisla cruzadas inútiles hacia la otra rama).
 */
export function depRelevantToPerfil(
  d: ConfiguradorDependencia,
  camposByKey: Map<string, CampoPerfilPick>,
  perfil: ConfiguradorPerfilId,
): boolean {
  if (d.activo === false) return false;
  if (d.perfil && d.perfil !== perfil) return false;
  const parentKey = parentKeysOfDep(d).parentKey;
  const childKey = d.childCampoKey;
  const child = camposByKey.get(childKey);
  const parent = parentKey ? camposByKey.get(parentKey) : undefined;

  if (child && !campoPerteneceAPerfil(child, perfil)) return false;

  const optKey = opcionKeyOfDep(d);
  const expectedFabricaOpt = fabricaKeyForPerfil(perfil);
  const fabricaOptForPerfil =
    parentKey === PASO0_FABRICA_KEY &&
    d.condicion === "option_selected" &&
    !!optKey &&
    optKey === expectedFabricaOpt;

  // FABRICA = opción de la otra rama → no mostrar en este perfil
  if (
    parentKey === PASO0_FABRICA_KEY &&
    d.condicion === "option_selected" &&
    optKey &&
    optKey !== expectedFabricaOpt
  ) {
    const optPerfil = perfilFromFabricaKey(optKey);
    if (optPerfil && optPerfil !== perfil) return false;
  }

  // Cascada interna FAVRE legacy: no listar en Otras.
  // Excepciones: perfil='otras' explícito (CRUD Simple) o ancla fábrica→opción Otras.
  if (
    perfil === "otras" &&
    d.perfil !== "otras" &&
    !fabricaOptForPerfil &&
    parentKey &&
    FAVRE_CASCADE_KEYS.has(parentKey) &&
    FAVRE_CASCADE_KEYS.has(childKey)
  ) {
    return false;
  }

  if (parent && !isPaso0Campo(parent) && !isSharedCommercialCampo(parent)) {
    if (perfil === "favre" && !parent.aplicaFavre) return false;
    if (perfil === "otras" && !parent.aplicaOtras) return false;
  }

  // Legacy ambigua: no mostrar null en ambos a ciegas (ver helper).
  // Ancla fábrica con opción de esta rama: relevante aunque extremos sean duales.
  if (!d.perfil) {
    if (
      !fabricaOptForPerfil &&
      !legacyNullDepRelevantToPerfil(parent, child, perfil)
    ) {
      return false;
    }
  }

  // always → raíz: ruido cosmético; se sigue listando como soft warn, pero
  // fuera del perfil de la rama hija no aporta.
  if (d.condicion === "always" && child?.esRaiz) {
    if (child.key === PASO0_FABRICA_KEY) return false;
  }

  return true;
}

export function filterDepsForPerfil(
  deps: ConfiguradorDependencia[],
  campos: Pick<
    ConfiguradorCampo,
    "key" | "aplicaFavre" | "aplicaOtras" | "esRaiz" | "membershipPerfiles"
  >[],
  perfil: ConfiguradorPerfilId,
): ConfiguradorDependencia[] {
  const byKey = new Map(campos.map((c) => [c.key, c]));
  return deps.filter((d) => depRelevantToPerfil(d, byKey, perfil));
}

/** Una fila por campo: collapsa JOIN PerfilCampo / merge FAVRE+Otras. */
export function uniqueCamposByIdOrKey<T extends { id?: number; key: string }>(
  campos: T[],
): T[] {
  const seenIds = new Set<number>();
  const seenKeys = new Set<string>();
  const out: T[] = [];
  for (const c of campos) {
    if (typeof c.id === "number" && seenIds.has(c.id)) continue;
    if (seenKeys.has(c.key)) continue;
    if (typeof c.id === "number") seenIds.add(c.id);
    seenKeys.add(c.key);
    out.push(c);
  }
  return out;
}

type ParentCampoCandidate = Pick<
  ConfiguradorCampo,
  "key" | "aplicaFavre" | "aplicaOtras" | "membershipPerfiles"
> & { id?: number };

/**
 * Opciones de campo padre para el perfil activo: membresía de esa rama,
 * un campo una vez (LINEA dual no se lista por FAVRE y por Otras).
 * Tras copy-on-write, cada perfil ve su propia fila (`linea` / `linea__otras`).
 */
export function parentCampoCandidatesForPerfil<T extends ParentCampoCandidate>(
  campos: T[],
  perfil: ConfiguradorPerfilId,
): T[] {
  return uniqueCamposByIdOrKey(
    campos.filter((c) => campoPerteneceAPerfil(c, perfil)),
  );
}

export function partitionCamposForPerfilBuilder(
  campos: ConfiguradorCampo[],
  perfil: ConfiguradorPerfilId,
): {
  paso0: ConfiguradorCampo[];
  rama: ConfiguradorCampo[];
  shared: ConfiguradorCampo[];
} {
  const paso0: ConfiguradorCampo[] = [];
  const rama: ConfiguradorCampo[] = [];
  const shared: ConfiguradorCampo[] = [];
  for (const c of uniqueCamposByIdOrKey(campos)) {
    const section = sectionForCampoInPerfil(c, perfil);
    if (!section) continue;
    if (section === "paso0") {
      paso0.push(c);
      continue;
    }
    if (section === "shared") {
      // Incluye operativos (stock/sucursal): bloque compartido, no DnD de rama.
      shared.push(c);
      continue;
    }
    // rama: solo campos del wizard builder (excl. grupos virtuales operativos)
    if (!isWizardBuilderCampo(c)) continue;
    rama.push(c);
  }
  const byOrden = (a: ConfiguradorCampo, b: ConfiguradorCampo) =>
    a.orden - b.orden || a.key.localeCompare(b.key);
  return {
    paso0: paso0.sort(byOrden),
    rama: rama.sort(byOrden),
    shared: shared.sort(byOrden),
  };
}

// ---------------------------------------------------------------------------
// DNF / grupos (AND dentro del grupo, OR entre grupos)
// ---------------------------------------------------------------------------

export type DepGrupo = {
  grupoId: string;
  deps: ConfiguradorDependencia[];
};

/** Agrupa reglas por grupoId; sin id → singleton estable por fila. */
export function groupDepsByGrupoId(
  deps: ConfiguradorDependencia[],
): DepGrupo[] {
  const sorted = [...deps].sort(
    (a, b) => (a.orden - b.orden) || (a.id - b.id),
  );
  const map = new Map<string, ConfiguradorDependencia[]>();
  const order: string[] = [];
  for (const d of sorted) {
    const gid = d.grupoId || `__singleton_${d.id}`;
    if (!map.has(gid)) {
      map.set(gid, []);
      order.push(gid);
    }
    map.get(gid)!.push(d);
  }
  return order.map((grupoId) => ({ grupoId, deps: map.get(grupoId)! }));
}

/** Resumen legible: `(A AND B) OR C`. */
export function dnfVisibilitySummary(
  deps: ConfiguradorDependencia[],
  labels: LabelMaps,
): string {
  const groups = groupDepsByGrupoId(deps);
  if (!groups.length) return "";
  return groups
    .map((g) => {
      const chips = g.deps.map((d) => humanVisibilityChip(d, labels));
      if (chips.length === 1) return chips[0];
      return `(${chips.join(" AND ")})`;
    })
    .join(" OR ");
}

/** UUID v4 para un grupo OR nuevo (cliente; el BE también puede asignar). */
export function newDepGrupoId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback no-crypto (tests / entornos viejos)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
